import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Grid,
  Alert,
  useTheme,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../util/auth';
import { useNavigate } from 'react-router-dom';
import WebLayout from '../components/WebLayout';
import styled from '@emotion/styled';
import { alpha } from '@mui/material/styles';

const AdminContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  minHeight: '100vh',
  background: theme.palette.background.default,
  padding: '32px',
}));

const PageHeader = styled(Box)({
  marginBottom: '32px',
});

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  background: theme.palette.background.paper,
  borderRadius: '8px',
  border: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
}));

const StyledHeaderCell = styled(TableCell)(({ theme }) => ({
  background: theme.palette.mode === 'dark' ? '#1F2937' : '#F9FAFB',
  color: theme.palette.text.primary,
  fontWeight: 600,
  fontSize: '0.8125rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  borderBottom: 'none',
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  color: theme.palette.text.secondary,
  borderBottom: 'none',
}));

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
  'Nationwide'
];

const Admin = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingBank, setEditingBank] = useState(null);
  const [importStatus, setImportStatus] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    amount: '',
    directDeposit: '',
    dailyAmountRequired: '',
    monthlyFee: '',
    timeframe: '',
    states: [],
    difficulty: 'Medium',
    description: '',
    terms: '',
    url: '',
    expiration: '',
    hardPull: false,
    chexSystems: false,
    successRate: 0,
  });

  useEffect(() => {
    if (!isAdmin) {
      navigate('/bonus-watcher');
      return;
    }
    fetchBanks();
  }, [isAdmin, navigate]);

  const fetchBanks = async () => {
    try {
      setLoading(true);
      const banksRef = collection(db, 'banks');
      const snapshot = await getDocs(banksRef);
      const banksData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setBanks(banksData);
    } catch (error) {
      console.error('Error fetching banks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (bank = null) => {
    if (bank) {
      // Convert expiration date to YYYY-MM-DD format for date input
      let expirationDate = '';
      if (bank.expiration) {
        try {
          if (typeof bank.expiration === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(bank.expiration)) {
            expirationDate = bank.expiration;
          } else {
            // Parse the date and format it without timezone conversion
            const date = new Date(bank.expiration);
            const year = date.getUTCFullYear();
            const month = String(date.getUTCMonth() + 1).padStart(2, '0');
            const day = String(date.getUTCDate()).padStart(2, '0');
            expirationDate = `${year}-${month}-${day}`;
          }
        } catch (e) {
          console.error('Error parsing expiration date:', e);
        }
      }

      setEditingBank(bank);
      setFormData({
        name: bank.name || '',
        amount: bank.amount || '',
        directDeposit: bank.directDeposit || '',
        dailyAmountRequired: bank.dailyAmountRequired || '',
        monthlyFee: bank.monthlyFee || '',
        timeframe: bank.timeframe || '',
        states: bank.states || [],
        difficulty: bank.difficulty || 'Medium',
        description: bank.description || '',
        terms: bank.terms || '',
        url: bank.url || '',
        expiration: expirationDate,
        hardPull: bank.hardPull || false,
        chexSystems: bank.chexSystems || false,
        successRate: bank.successRate || 0,
      });
    } else {
      setEditingBank(null);
      setFormData({
        name: '',
        amount: '',
        directDeposit: '',
        dailyAmountRequired: '',
        monthlyFee: '',
        timeframe: '',
        states: [],
        difficulty: 'Medium',
        description: '',
        terms: '',
        url: '',
        expiration: '',
        hardPull: false,
        chexSystems: false,
        successRate: 0,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingBank(null);
  };

  const handleSave = async () => {
    try {
      const bankData = {
        ...formData,
        amount: Number(formData.amount),
        directDeposit: Number(formData.directDeposit),
        dailyAmountRequired: Number(formData.dailyAmountRequired) || 0,
        monthlyFee: Number(formData.monthlyFee) || 0,
        timeframe: Number(formData.timeframe),
        successRate: Number(formData.successRate),
        createdBy: user.uid,
        status: 'verified',
        updatedAt: new Date().toISOString(),
      };

      if (editingBank) {
        // Update existing bank
        const bankRef = doc(db, 'banks', editingBank.id);
        await updateDoc(bankRef, bankData);
      } else {
        // Add new bank
        bankData.createdAt = new Date().toISOString();
        await addDoc(collection(db, 'banks'), bankData);
      }

      fetchBanks();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving bank:', error);
      alert('Failed to save bank');
    }
  };

  const handleDelete = async (bankId) => {
    if (!window.confirm('Are you sure you want to delete this bank bonus?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'banks', bankId));
      fetchBanks();
    } catch (error) {
      console.error('Error deleting bank:', error);
      alert('Failed to delete bank');
    }
  };

  const handleExport = () => {
    try {
      // Create CSV header
      const headers = [
        'name',
        'amount',
        'directDeposit',
        'dailyAmountRequired',
        'monthlyFee',
        'timeframe',
        'difficulty',
        'states',
        'successRate',
        'expiration',
        'url',
        'description',
        'terms',
        'hardPull',
        'chexSystems',
        'updatedAt',
      ];

      // Convert banks to CSV rows
      const csvRows = [headers.join(',')];

      banks.forEach((bank) => {
        const row = [
          `"${bank.name || ''}"`,
          bank.amount || 0,
          bank.directDeposit || 0,
          bank.dailyAmountRequired || 0,
          bank.monthlyFee || 0,
          bank.timeframe || 0,
          `"${bank.difficulty || 'Medium'}"`,
          `"${bank.states?.join(';') || ''}"`,
          bank.successRate || 0,
          `"${bank.expiration || ''}"`,
          `"${bank.url || ''}"`,
          `"${(bank.description || '').replace(/"/g, '""')}"`,
          `"${(bank.terms || '').replace(/"/g, '""')}"`,
          bank.hardPull ? 'true' : 'false',
          bank.chexSystems ? 'true' : 'false',
          `"${bank.updatedAt || ''}"`,
        ];
        csvRows.push(row.join(','));
      });

      // Create blob and download
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', `bank_bonuses_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setImportStatus({ type: 'success', message: 'Banks exported successfully!' });
      setTimeout(() => setImportStatus(null), 3000);
    } catch (error) {
      console.error('Error exporting banks:', error);
      setImportStatus({ type: 'error', message: 'Failed to export banks' });
      setTimeout(() => setImportStatus(null), 5000);
    }
  };

  const handleImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = e.target.result;
        const lines = text.split('\n');

        // Parse CSV
        const headers = lines[0].split(',').map(h => h.trim());
        const parsedBanks = [];

        for (let i = 1; i < lines.length; i++) {
          if (!lines[i].trim()) continue;

          // Handle quoted fields properly
          const values = [];
          let currentValue = '';
          let insideQuotes = false;

          for (let char of lines[i]) {
            if (char === '"') {
              insideQuotes = !insideQuotes;
            } else if (char === ',' && !insideQuotes) {
              values.push(currentValue.trim());
              currentValue = '';
            } else {
              currentValue += char;
            }
          }
          values.push(currentValue.trim());

          // Create bank object
          const bankData = {};
          headers.forEach((header, index) => {
            let value = values[index] || '';
            // Remove surrounding quotes
            if (value.startsWith('"') && value.endsWith('"')) {
              value = value.slice(1, -1);
            }
            // Unescape double quotes
            value = value.replace(/""/g, '"');

            if (header === 'name') bankData.name = value;
            else if (header === 'amount') bankData.amount = Number(value) || 0;
            else if (header === 'directDeposit') bankData.directDeposit = Number(value) || 0;
            else if (header === 'dailyAmountRequired') bankData.dailyAmountRequired = Number(value) || 0;
            else if (header === 'monthlyFee') bankData.monthlyFee = Number(value) || 0;
            else if (header === 'timeframe') bankData.timeframe = Number(value) || 0;
            else if (header === 'difficulty') bankData.difficulty = value || 'Medium';
            else if (header === 'states') bankData.states = value ? value.split(';').map(s => s.trim()) : [];
            else if (header === 'successRate') bankData.successRate = Number(value) || 0;
            else if (header === 'expiration') bankData.expiration = value;
            else if (header === 'url') bankData.url = value;
            else if (header === 'description') bankData.description = value;
            else if (header === 'terms') bankData.terms = value;
            else if (header === 'hardPull') bankData.hardPull = value === 'true';
            else if (header === 'chexSystems') bankData.chexSystems = value === 'true';
            else if (header === 'updatedAt') bankData.updatedAt = value;
          });

          parsedBanks.push(bankData);
        }

        // Import banks
        let updated = 0;
        let created = 0;

        for (const bankData of parsedBanks) {
          if (!bankData.name) continue;

          // Check if bank exists
          const existingBank = banks.find(b => b.name.toLowerCase() === bankData.name.toLowerCase());

          const fullBankData = {
            ...bankData,
            createdBy: user.uid,
            status: 'verified',
            updatedAt: new Date().toISOString(),
          };

          if (existingBank) {
            // Update existing bank
            const bankRef = doc(db, 'banks', existingBank.id);
            await updateDoc(bankRef, fullBankData);
            updated++;
          } else {
            // Create new bank
            fullBankData.createdAt = new Date().toISOString();
            await addDoc(collection(db, 'banks'), fullBankData);
            created++;
          }
        }

        await fetchBanks();
        setImportStatus({
          type: 'success',
          message: `Import complete! Created: ${created}, Updated: ${updated}`
        });
        setTimeout(() => setImportStatus(null), 5000);
      } catch (error) {
        console.error('Error importing banks:', error);
        setImportStatus({ type: 'error', message: 'Failed to import banks. Check file format.' });
        setTimeout(() => setImportStatus(null), 5000);
      }
    };

    reader.readAsText(file);
    // Reset input
    event.target.value = '';
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <WebLayout>
      <AdminContainer>
        <PageHeader>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h4" sx={{ color: theme.palette.text.primary, fontWeight: 700 }}>
              Admin Panel - Manage Bank Bonuses
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                onClick={handleExport}
                sx={{
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  '&:hover': {
                    borderColor: theme.palette.primary.dark,
                    background: alpha(theme.palette.primary.main, 0.1),
                  },
                }}
              >
                Export CSV
              </Button>
              <Button
                variant="outlined"
                startIcon={<FileUploadIcon />}
                component="label"
                sx={{
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  '&:hover': {
                    borderColor: theme.palette.primary.dark,
                    background: alpha(theme.palette.primary.main, 0.1),
                  },
                }}
              >
                Import CSV
                <input
                  type="file"
                  hidden
                  accept=".csv"
                  onChange={handleImport}
                />
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                sx={{
                  background: theme.palette.primary.main,
                  color: '#fff',
                  '&:hover': {
                    background: theme.palette.primary.mainDark,
                  },
                }}
              >
                Add New Bank
              </Button>
            </Stack>
          </Stack>
          <Alert severity="info" sx={{ mt: 2 }}>
            Logged in as admin: {user?.email}
          </Alert>
          {importStatus && (
            <Alert severity={importStatus.type} sx={{ mt: 2 }}>
              {importStatus.message}
            </Alert>
          )}
        </PageHeader>

        <StyledTableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <StyledHeaderCell>Bank Name</StyledHeaderCell>
                <StyledHeaderCell>Bonus Amount</StyledHeaderCell>
                <StyledHeaderCell>Direct Deposit</StyledHeaderCell>
                <StyledHeaderCell>Timeframe (Days)</StyledHeaderCell>
                <StyledHeaderCell>Difficulty</StyledHeaderCell>
                <StyledHeaderCell>States</StyledHeaderCell>
                <StyledHeaderCell>Last Updated</StyledHeaderCell>
                <StyledHeaderCell>Actions</StyledHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <StyledTableCell colSpan={8} align="center">
                    Loading...
                  </StyledTableCell>
                </TableRow>
              ) : banks.length === 0 ? (
                <TableRow>
                  <StyledTableCell colSpan={8} align="center">
                    No banks found. Add your first bank bonus!
                  </StyledTableCell>
                </TableRow>
              ) : (
                banks.map((bank) => (
                  <TableRow key={bank.id} hover>
                    <StyledTableCell>
                      <Typography sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
                        {bank.name}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
                        ${bank.amount?.toLocaleString()}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      ${bank.directDeposit?.toLocaleString()}
                    </StyledTableCell>
                    <StyledTableCell>{bank.timeframe} days</StyledTableCell>
                    <StyledTableCell>
                      <Chip
                        label={bank.difficulty}
                        size="small"
                        sx={{
                          background:
                            bank.difficulty === 'Easy'
                              ? alpha(theme.palette.success.main, 0.15)
                              : bank.difficulty === 'Hard'
                              ? alpha(theme.palette.error.main, 0.15)
                              : alpha(theme.palette.warning.main, 0.15),
                          color:
                            bank.difficulty === 'Easy'
                              ? theme.palette.success.main
                              : bank.difficulty === 'Hard'
                              ? theme.palette.error.main
                              : theme.palette.warning.main,
                          border: 'none',
                        }}
                      />
                    </StyledTableCell>
                    <StyledTableCell>
                      {bank.states?.slice(0, 3).join(', ')}
                      {bank.states?.length > 3 && ` +${bank.states.length - 3}`}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography sx={{ color: theme.palette.text.primarySecondary, fontSize: '0.875rem' }}>
                        {bank.updatedAt
                          ? new Date(bank.updatedAt).toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                            })
                          : 'N/A'}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Stack direction="row" spacing={1}>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(bank)}
                          sx={{ color: theme.palette.primary.main }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(bank.id)}
                          sx={{ color: theme.palette.error.main }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </StyledTableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </StyledTableContainer>

        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              background: theme.palette.background.paper,
              color: theme.palette.text.primary,
            },
          }}
        >
          <DialogTitle>
            {editingBank ? 'Edit Bank Bonus' : 'Add New Bank Bonus'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Bank Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  sx={{
                    '& .MuiInputLabel-root': { color: theme.palette.text.primarySecondary },
                    '& .MuiOutlinedInput-root': { color: theme.palette.text.primary },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Bonus Amount"
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required
                  sx={{
                    '& .MuiInputLabel-root': { color: theme.palette.text.primarySecondary },
                    '& .MuiOutlinedInput-root': { color: theme.palette.text.primary },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Direct Deposit Required"
                  type="number"
                  value={formData.directDeposit}
                  onChange={(e) =>
                    setFormData({ ...formData, directDeposit: e.target.value })
                  }
                  sx={{
                    '& .MuiInputLabel-root': { color: theme.palette.text.primarySecondary },
                    '& .MuiOutlinedInput-root': { color: theme.palette.text.primary },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Daily Amount Required"
                  type="number"
                  value={formData.dailyAmountRequired}
                  onChange={(e) =>
                    setFormData({ ...formData, dailyAmountRequired: e.target.value })
                  }
                  sx={{
                    '& .MuiInputLabel-root': { color: theme.palette.text.primarySecondary },
                    '& .MuiOutlinedInput-root': { color: theme.palette.text.primary },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Monthly Fee"
                  type="number"
                  value={formData.monthlyFee}
                  onChange={(e) =>
                    setFormData({ ...formData, monthlyFee: e.target.value })
                  }
                  sx={{
                    '& .MuiInputLabel-root': { color: theme.palette.text.primarySecondary },
                    '& .MuiOutlinedInput-root': { color: theme.palette.text.primary },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Timeframe (Days)"
                  type="number"
                  value={formData.timeframe}
                  onChange={(e) => setFormData({ ...formData, timeframe: e.target.value })}
                  required
                  sx={{
                    '& .MuiInputLabel-root': { color: theme.palette.text.primarySecondary },
                    '& .MuiOutlinedInput-root': { color: theme.palette.text.primary },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: theme.palette.text.primarySecondary }}>Difficulty</InputLabel>
                  <Select
                    value={formData.difficulty}
                    label="Difficulty"
                    onChange={(e) =>
                      setFormData({ ...formData, difficulty: e.target.value })
                    }
                    sx={{ color: theme.palette.text.primary }}
                  >
                    <MenuItem value="Easy">Easy</MenuItem>
                    <MenuItem value="Medium">Medium</MenuItem>
                    <MenuItem value="Hard">Hard</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: theme.palette.text.primarySecondary }}>States</InputLabel>
                  <Select
                    multiple
                    value={formData.states}
                    label="States"
                    onChange={(e) => setFormData({ ...formData, states: e.target.value })}
                    sx={{ color: theme.palette.text.primary }}
                    renderValue={(selected) => selected.join(', ')}
                  >
                    {US_STATES.map((state) => (
                      <MenuItem key={state} value={state}>
                        <Checkbox
                          checked={formData.states.indexOf(state) > -1}
                          sx={{
                            color: theme.palette.primary.main,
                            '&.Mui-checked': {
                              color: theme.palette.primary.main,
                            },
                          }}
                        />
                        {state}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Success Rate (%)"
                  type="number"
                  value={formData.successRate}
                  onChange={(e) =>
                    setFormData({ ...formData, successRate: e.target.value })
                  }
                  inputProps={{ min: 0, max: 100 }}
                  sx={{
                    '& .MuiInputLabel-root': { color: theme.palette.text.primarySecondary },
                    '& .MuiOutlinedInput-root': { color: theme.palette.text.primary },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Expiration Date"
                  type="date"
                  value={formData.expiration}
                  onChange={(e) => setFormData({ ...formData, expiration: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiInputLabel-root': { color: theme.palette.text.primarySecondary },
                    '& .MuiOutlinedInput-root': { color: theme.palette.text.primary },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Bank URL"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  sx={{
                    '& .MuiInputLabel-root': { color: theme.palette.text.primarySecondary },
                    '& .MuiOutlinedInput-root': { color: theme.palette.text.primary },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  sx={{
                    '& .MuiInputLabel-root': { color: theme.palette.text.primarySecondary },
                    '& .MuiOutlinedInput-root': { color: theme.palette.text.primary },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Terms & Conditions"
                  multiline
                  rows={3}
                  value={formData.terms}
                  onChange={(e) => setFormData({ ...formData, terms: e.target.value })}
                  sx={{
                    '& .MuiInputLabel-root': { color: theme.palette.text.primarySecondary },
                    '& .MuiOutlinedInput-root': { color: theme.palette.text.primary },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.hardPull}
                      onChange={(e) =>
                        setFormData({ ...formData, hardPull: e.target.checked })
                      }
                      sx={{ color: theme.palette.text.primarySecondary }}
                    />
                  }
                  label="Hard Pull"
                  sx={{ color: theme.palette.text.primarySecondary }}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.chexSystems}
                      onChange={(e) =>
                        setFormData({ ...formData, chexSystems: e.target.checked })
                      }
                      sx={{ color: theme.palette.text.primarySecondary }}
                    />
                  }
                  label="ChexSystems Check"
                  sx={{ color: theme.palette.text.primarySecondary }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} sx={{ color: theme.palette.text.primarySecondary }}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              sx={{
                background: theme.palette.primary.main,
                color: '#fff',
                '&:hover': {
                  background: theme.palette.primary.mainDark,
                },
              }}
            >
              {editingBank ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>
      </AdminContainer>
    </WebLayout>
  );
};

export default Admin;
